// DIAGNOSTIC VERSION - Better error messages
// Replace AddCustomerModal.js with this temporarily to see exact error

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { customerAPI } from '../../../services/customerAPI';

export default function AddCustomerModal({ onClose, onSave, businessData }) {
  const [activeMethod, setActiveMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [pastedText, setPastedText] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '+44',
    phone: '',
    email: '',
    source: 'instagram',
    notes: '',
    created_at: new Date().toISOString().split('T')[0]
  });

  const handleAIExtract = async () => {
    if (!pastedText.trim()) {
      setError('Please paste some text first');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🔍 DIAGNOSTIC: Calling AI extraction...');
      console.log('🔍 DIAGNOSTIC: Text to extract:', pastedText);
      console.log('🔍 DIAGNOSTIC: Text length:', pastedText.length);
      
      const response = await customerAPI.extract(pastedText);
      
      console.log('🔍 DIAGNOSTIC: Full response:', response);
      console.log('🔍 DIAGNOSTIC: Response.success:', response.success);
      console.log('🔍 DIAGNOSTIC: Response.data:', response.data);
      console.log('🔍 DIAGNOSTIC: Response.error:', response.error);
      
      if (response.success && response.data) {
        // Success - parse the data
        let phoneNumber = response.data.phone || '';
        let countryCode = '+44';
        
        if (phoneNumber.startsWith('+')) {
          const match = phoneNumber.match(/^(\+\d{1,3})\s?(.+)$/);
          if (match) {
            countryCode = match[1];
            phoneNumber = match[2];
          }
        }
        
        setFormData({
          name: response.data.name || '',
          countryCode: countryCode,
          phone: phoneNumber.replace(/\D/g, ''),
          email: response.data.email || '',
          source: response.data.source || 'instagram',
          notes: response.data.notes || pastedText,
          created_at: new Date().toISOString().split('T')[0]
        });
        setActiveMethod('review');
        setError('');
      } else {
        // Show the actual error from backend
        const errorMsg = response.error || 'Unknown error from backend';
        console.error('🔍 DIAGNOSTIC: Backend error:', errorMsg);
        setError(`Backend Error: ${errorMsg}`);
      }
    } catch (err) {
      console.error('🔍 DIAGNOSTIC: Exception caught:', err);
      console.error('🔍 DIAGNOSTIC: Error message:', err.message);
      console.error('🔍 DIAGNOSTIC: Error stack:', err.stack);
      setError(`Network Error: ${err.message}. Check if backend is running on port 5000.`);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, phone: value });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Customer name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const fullPhone = formData.phone ? `${formData.countryCode} ${formData.phone}` : '';
      
      const dataToSave = {
        name: formData.name,
        phone: fullPhone,
        email: formData.email,
        source: formData.source,
        notes: formData.notes,
        created_at: new Date(formData.created_at).toISOString()
      };
      
      console.log('💾 Saving customer:', dataToSave);
      await onSave(dataToSave);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save customer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderMethodSelection = () => (
    <div className="p-6 space-y-4">
      <button
        onClick={() => setActiveMethod('paste')}
        className="w-full p-5 bg-white border-2 border-gray-300 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-all group text-left"
      >
        <div className="flex items-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-blue-600 transition-colors">
            <i className="fas fa-paste text-blue-600 text-xl group-hover:text-white"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900 mb-1">AI Paste & Extract</p>
            <p className="text-xs text-gray-600">Paste any message, AI extracts details instantly</p>
          </div>
          <i className="fas fa-chevron-right text-gray-400 text-lg"></i>
        </div>
      </button>

      <button
        onClick={() => setActiveMethod('manual')}
        className="w-full p-5 bg-white border-2 border-gray-300 rounded-lg hover:border-green-600 hover:bg-green-50 transition-all group text-left"
      >
        <div className="flex items-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-green-600 transition-colors">
            <i className="fas fa-keyboard text-green-600 text-xl group-hover:text-white"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-gray-900 mb-1">Manual Entry</p>
            <p className="text-xs text-gray-600">Type in customer details yourself</p>
          </div>
          <i className="fas fa-chevron-right text-gray-400 text-lg"></i>
        </div>
      </button>
    </div>
  );

  const renderPastePanel = () => (
    <div className="p-6 space-y-4">
      <button
        onClick={() => setActiveMethod(null)}
        className="flex items-center text-sm text-gray-600 hover:text-gray-900"
      >
        <i className="fas fa-arrow-left mr-2"></i>
        Back
      </button>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Paste Message
        </label>
        <textarea
          value={pastedText}
          onChange={(e) => setPastedText(e.target.value)}
          placeholder="Hi I am Sarah I want hair transplant, my phone number is 89879879879 and I am from UK"
          rows={6}
          className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none"
        />
        <p className="text-xs text-gray-500 mt-2">
          <i className="fas fa-lightbulb mr-1"></i>
          Paste Instagram DMs, WhatsApp messages, emails, or any text
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700 font-medium mb-2">
            <i className="fas fa-exclamation-circle mr-2"></i>
            Error Details:
          </p>
          <p className="text-xs text-red-600 whitespace-pre-wrap font-mono">
            {error}
          </p>
          <p className="text-xs text-red-600 mt-2">
            💡 <strong>Workaround:</strong> Click "Back" and choose "Manual Entry" instead
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleAIExtract}
          disabled={loading || !pastedText.trim()}
          className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Extracting...
            </>
          ) : (
            <>
              <i className="fas fa-magic mr-2"></i>
              AI Extract
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderFormPanel = () => (
    <div className="p-6 space-y-4">
      {activeMethod === 'review' && (
        <>
          <button
            onClick={() => setActiveMethod('paste')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back
          </button>
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-green-700">
              <i className="fas fa-check-circle mr-2"></i>
              AI extracted customer details. Review and edit if needed.
            </p>
          </div>
        </>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Customer Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Sarah Johnson"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Phone Number
          </label>
          <div className="flex gap-2">
            <select
              value={formData.countryCode}
              onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
              className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            >
              <option value="+44">🇬🇧 +44</option>
              <option value="+1">🇺🇸 +1</option>
              <option value="+91">🇮🇳 +91</option>
            </select>
            <input
              type="tel"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="7700123456"
              pattern="[0-9]*"
              inputMode="numeric"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Email Address
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="e.g., sarah@email.com"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Source
          </label>
          <select
            value={formData.source}
            onChange={(e) => setFormData({ ...formData, source: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          >
            <option value="instagram">Instagram</option>
            <option value="facebook">Facebook</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="phone">Phone Call</option>
            <option value="website">Website</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Date Added <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.created_at}
            onChange={(e) => setFormData({ ...formData, created_at: e.target.value })}
            max={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="e.g., Interested in hair transplant from UK"
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent resize-none"
          />
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            <i className="fas fa-exclamation-circle mr-2"></i>
            {error}
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              Save Customer
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeMethod) {
      case 'paste':
        return renderPastePanel();
      case 'manual':
      case 'review':
        return renderFormPanel();
      default:
        return renderMethodSelection();
    }
  };

  return createPortal(
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      ></div>
      
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="sticky top-0 bg-white border-b border-gray-300 p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {activeMethod === 'paste' ? 'AI Paste Extract' :
                 activeMethod === 'review' ? 'Review Customer' :
                 activeMethod === 'manual' ? 'Manual Entry' :
                 'Add Customer'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {activeMethod === 'paste' ? 'Paste any message' :
                 activeMethod === 'review' ? 'Check AI extraction' :
                 activeMethod === 'manual' ? 'Fill in details' :
                 'Choose your preferred method'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {renderContent()}
      </div>

      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>,
    document.body
  );
}